import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getMeProfile,
  updateMyPreferences,
  type ConsumerMeProfile,
} from '@/lib/api';
import { setAuthState, getAuthState } from '@/lib/auth-store';
import { useTk } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';
import { useSelectedCity } from '@/lib/use-city';
import { BottomNav } from '@/components/BottomNav';
import { Button } from '@sabay/ui-kit';

type Locale = 'zh-CN' | 'km-KH' | 'en';

const LOCALE_OPTIONS: Array<{ code: Locale; label: string }> = [
  { code: 'zh-CN', label: '中文' },
  { code: 'km-KH', label: 'ភាសាខ្មែរ' },
  { code: 'en', label: 'English' },
];

export function MePage(): React.ReactElement {
  const tk = useTk();
  const navigate = useNavigate();
  const auth = useAuth();
  const [cityCode] = useSelectedCity();
  const [profile, setProfile] = useState<ConsumerMeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingLocale, setSavingLocale] = useState<Locale | null>(null);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.token) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    getMeProfile(auth.token, ac.signal)
      .then((res) => setProfile(res.user))
      .catch((err: Error) => {
        if (ac.signal.aborted) return;
        setError(err.message);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [auth.status, auth.token]);

  async function handleLocaleChange(code: Locale | null): Promise<void> {
    if (!auth.token) return;
    setSavingLocale(code);
    setError(null);
    try {
      const res = await updateMyPreferences({ preferredLocale: code }, auth.token);
      setProfile(res.user);
      const current = getAuthState();
      if (current.user) {
        setAuthState({
          user: { ...current.user, preferredLocale: res.user.preferredLocale },
        });
      }
    } catch (err) {
      const apiErr = err as Error & { code?: string };
      setError(`${apiErr.code ?? 'ERROR'}: ${apiErr.message}`);
    } finally {
      setSavingLocale(null);
    }
  }

  const displayName = profile
    ? profile.firstName
      ? `${profile.firstName}${profile.lastName ? ' ' + profile.lastName : ''}`
      : '—'
    : '';
  const initial = (displayName || '?').slice(0, 1).toUpperCase();

  return (
    <main className="min-h-screen pb-24">
      <header className="sticky top-0 z-20 px-5 pt-5 pb-3 bg-clay-50/85 backdrop-blur-md flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(-1)}
          className="grid h-9 w-9 place-items-center rounded-full border border-clay-200 bg-white p-0 text-clay-700"
          aria-label="back"
        >
          <BackGlyph />
        </Button>
        <span className="eyebrow flex-1">Profile</span>
      </header>

      <div className="px-5 mt-2 space-y-6">
        {auth.status !== 'authenticated' ? (
          <div className="surface-card border-dashed p-12 text-center text-clay-500 text-sm">
            {tk('me.unauth.body')}
          </div>
        ) : loading ? (
          <div className="text-center py-6 text-clay-400 text-sm">
            {tk('common.loading')}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-danger-500/40 bg-danger-500/10 p-4 text-sm text-danger-500">
            <p>{tk('common.error.generic')}</p>
            <p className="text-xs opacity-75 mt-1 break-all">{error}</p>
          </div>
        ) : profile ? (
          <>
            <section className="flex items-center gap-4 pt-2">
              <div
                className="grid h-16 w-16 shrink-0 place-items-center rounded-full overflow-hidden font-serif text-2xl font-semibold text-clay-900/90"
                style={{
                  background:
                    'linear-gradient(135deg, #FFE3BD 0%, #FFB050 60%, #E5741F 100%)',
                }}
                aria-hidden
              >
                {initial}
              </div>
              <div className="min-w-0">
                <h1 className="display-heading text-2xl leading-tight truncate">
                  {displayName}
                </h1>
                {profile.username ? (
                  <p className="text-sm text-clay-500 truncate">@{profile.username}</p>
                ) : null}
                <p className="text-[11px] text-clay-400 mt-0.5">
                  Telegram <code className="font-mono">{profile.telegramId}</code>
                </p>
              </div>
            </section>

            <section className="space-y-2">
              <span className="eyebrow">Language</span>
              <p className="text-xs text-clay-500">
                {tk('me.preferences.locale.hint')}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={savingLocale !== null}
                  onClick={() => void handleLocaleChange(null)}
                  className={`chip ${
                    profile.preferredLocale === null ? 'chip-active' : 'chip-idle'
                  }`}
                >
                  {tk('me.preferences.locale.auto')}
                  {profile.languageCode ? ` (${profile.languageCode})` : ''}
                </Button>
                {LOCALE_OPTIONS.map((o) => (
                  <Button
                    key={o.code}
                    type="button"
                    variant="ghost"
                    disabled={savingLocale !== null}
                    onClick={() => void handleLocaleChange(o.code)}
                    className={`chip ${
                      profile.preferredLocale === o.code ? 'chip-active' : 'chip-idle'
                    }`}
                  >
                    {o.label}
                    {savingLocale === o.code ? '…' : ''}
                  </Button>
                ))}
              </div>
            </section>

            <section className="space-y-2">
              <span className="eyebrow">City</span>
              <p className="text-sm text-clay-700">
                {tk('me.preferences.city.current')}:{' '}
                <code className="font-mono text-amber-700">{cityCode}</code>
              </p>
              <p className="text-xs text-clay-500">
                {tk('me.preferences.city.hint')}
              </p>
            </section>

            <nav className="surface-card overflow-hidden divide-y divide-clay-100">
              <RowLink to="/me/orders" label={tk('me.nav.orders')} />
              <RowLink to="/me/favorites" label={tk('me.nav.favorites')} />
              <RowLink to="/me/cs" label={tk('me.nav.customer_service')} />
              <RowLink to="/groupons" label={tk('me.nav.browse_groupons')} />
            </nav>

            <section className="space-y-1.5 pt-2">
              <span className="eyebrow">Account</span>
              <Row label={tk('me.profile.last_login')} value={profile.lastLoginAt ? new Date(profile.lastLoginAt).toLocaleString() : '—'} />
            </section>
          </>
        ) : null}
      </div>

      <BottomNav />
    </main>
  );
}

function RowLink({ to, label }: { to: string; label: string }): React.ReactElement {
  return (
    <Link
      to={to}
      className="flex items-center justify-between px-5 py-4 hover:bg-amber-50/40 transition-colors"
    >
      <span className="text-sm text-clay-900">{label}</span>
      <ChevronGlyph />
    </Link>
  );
}

function Row({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-clay-500 text-xs shrink-0">{label}</span>
      <span className="text-right text-sm break-all text-clay-800">{value}</span>
    </div>
  );
}

function ChevronGlyph(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#B89E78" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 4l4 4-4 4" />
    </svg>
  );
}

function BackGlyph(): React.ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10 4l-4 4 4 4" />
    </svg>
  );
}
